import moment from "moment";
import { SignRequest } from "models/interfaces";
import { SignRequestFilterBody } from "./SignRequestFiltersService";
import signRequestsFiltersTokens, {
  DEFAULT_TAG_COLOR,
  SIGN_REQUESTS_TAG_COLOR_KEYS,
  signRequestsTagColors,
  SignRequestsTagColor,
} from "./SignRequestsFilters.tokens";

const { table, tags: tagTokens } = signRequestsFiltersTokens;

/** Wire format of the filter dates and of an `<input type="date">`. */
const DATE_FORMAT = "YYYY-MM-DD";
/** How the list APIs send `createdOn`. */
const CREATED_ON_FORMAT = "MM/DD/YYYY HH:mm:ss";
const DISPLAY_DATE_FORMAT = "MMM D, YYYY";
const SHORT_DATE_FORMAT = "MMM D";

export type SignRequestsQuickRange = "d7" | "d30" | "month";
export type SignRequestsSortKey = "newest" | "oldest" | "expiring";

/**
 * The screen's own filter model. It differs from the API body in two ways: the
 * sort order lives beside it rather than inside it, and `quick` remembers that
 * a date range was picked as "Last 7 days" so the pill and the chip can say so
 * — the API only ever sees the two dates.
 */
export interface SignRequestsFilters {
  /** `UiSignStatus` values — `SENT`, `IN_PROGRESS`, … */
  status: string[];
  quick: SignRequestsQuickRange | null;
  /** `YYYY-MM-DD`, or empty. */
  from: string;
  to: string;
  signer: string;
  /** Sender emails. */
  senders: string[];
  /** Template ids. */
  templates: string[];
  /** Tag ids. */
  tags: string[];
}

export const EMPTY_SIGN_REQUESTS_FILTERS: SignRequestsFilters = {
  status: [],
  quick: null,
  from: "",
  to: "",
  signer: "",
  senders: [],
  templates: [],
  tags: [],
};

export interface SignRequestTag {
  id: string;
  name: string;
  color: SignRequestsTagColor;
  /** Requests carrying the tag. Only sent with the full tag list. */
  usageCount?: number;
  /** Whether this user may rename, recolor or delete it. */
  canManage?: boolean;
}

export interface SignRequestsView {
  id: string;
  name: string;
  filters: SignRequestsFilters;
  sort: SignRequestsSortKey;
  /** Shipped with the screen — cannot be renamed or deleted. */
  builtIn?: boolean;
}

export interface SignRequestsOption {
  value: string;
  label: string;
  color?: SignRequestsTagColor;
}

export interface SignRequestsFilterChip {
  key: string;
  label: string;
  value: string;
  /** The slice of the filters this chip stands for, reset to empty. */
  cleared: Partial<SignRequestsFilters>;
}

/** Names the chips need but the filters only hold ids / emails for. */
export interface SignRequestsFilterLookups {
  tags: SignRequestTag[];
  senders: SignRequestsOption[];
  templates: SignRequestsOption[];
}

/**
 * The statuses the filtered list understands. These are the API's *UI*
 * statuses, not the raw `status` on a request: the stored `Pending` is split
 * into "Sent" (nobody has signed) and "In progress" (someone has).
 */
export const SIGN_REQUESTS_STATUS_OPTIONS: SignRequestsOption[] = [
  { value: "DRAFT", label: "Draft" },
  { value: "SCHEDULED", label: "Scheduled" },
  { value: "SENT", label: "Sent" },
  { value: "IN_PROGRESS", label: "In progress" },
  { value: "AWAITING_APPROVAL", label: "Awaiting approval" },
  { value: "COMPLETED", label: "Completed" },
  { value: "DECLINED", label: "Declined" },
  { value: "EXPIRED", label: "Expired" },
  { value: "VOIDED", label: "Voided" },
];

/** Still waiting on someone — the statuses that can run out of time. */
const OPEN_UI_STATUSES = [
  "SCHEDULED",
  "SENT",
  "IN_PROGRESS",
  "AWAITING_APPROVAL",
];

export const ALL_REQUESTS_VIEW_ID = "all";
export const BATCHES_VIEW_ID = "batches";

/**
 * Views that ship with the screen. Only the first is a filter preset; "Bulk
 * batches" swaps the list for a different one.
 */
export const SIGN_REQUESTS_BUILT_IN_VIEWS: SignRequestsView[] = [
  {
    id: ALL_REQUESTS_VIEW_ID,
    name: "All requests",
    filters: EMPTY_SIGN_REQUESTS_FILTERS,
    sort: "newest",
    builtIn: true,
  },
  {
    id: BATCHES_VIEW_ID,
    name: "Bulk batches",
    filters: EMPTY_SIGN_REQUESTS_FILTERS,
    sort: "newest",
    builtIn: true,
  },
];

export const SIGN_REQUESTS_SORT_OPTIONS: {
  key: SignRequestsSortKey;
  label: string;
}[] = [
  { key: "newest", label: "Newest first" },
  { key: "oldest", label: "Oldest first" },
  { key: "expiring", label: "Expiring soonest" },
];

export const getSortLabel = (sort: SignRequestsSortKey) =>
  SIGN_REQUESTS_SORT_OPTIONS.find((option) => option.key === sort)?.label ?? "";

const toSortKey = (value?: string | null): SignRequestsSortKey => {
  const key = (value ?? "").toLowerCase();
  return key === "oldest" || key === "expiring" ? key : "newest";
};

export const SIGN_REQUESTS_QUICK_RANGES: {
  key: SignRequestsQuickRange;
  label: string;
}[] = [
  { key: "d7", label: "Last 7 days" },
  { key: "d30", label: "Last 30 days" },
  { key: "month", label: "This month" },
];

/** Start of a quick range; every one of them ends today. */
export const getQuickRangeStart = (quick: SignRequestsQuickRange) => {
  switch (quick) {
    case "d7":
      return moment().subtract(7, "days").format(DATE_FORMAT);
    case "d30":
      return moment().subtract(30, "days").format(DATE_FORMAT);
    default:
      return moment().startOf("month").format(DATE_FORMAT);
  }
};

export const getToday = () => moment().format(DATE_FORMAT);

/** Whatever a date picker hands back, as the filters' `YYYY-MM-DD` (or empty). */
export const toFilterDate = (value: Date | string | null | undefined) =>
  value ? moment(value).format(DATE_FORMAT) : "";

/**
 * The reason behind a rejected tag or view change. The API prefixes these with
 * a code — "TAG_NAME_TAKEN: a tag called 'Urgent' already exists." — which is
 * for code to branch on, not for the user to read.
 */
export const getFilterErrorReason = (error: any): string | null => {
  const status = error?.statusCode ?? error?.status ?? error?.code;
  const message = error?.message ?? error?.errorMessage;
  if ((status !== 400 && status !== 409) || typeof message !== "string") {
    return null;
  }
  const reason = message.replace(/^[A-Z][A-Z_]+:\s*/, "").trim();
  return reason ? reason.charAt(0).toUpperCase() + reason.slice(1) : null;
};

export const toggleValue = (values: string[], value: string) =>
  values.includes(value)
    ? values.filter((entry) => entry !== value)
    : [...values, value];

const formatShort = (date: string) =>
  moment(date, DATE_FORMAT).format(SHORT_DATE_FORMAT);

const labelsFor = (values: string[], options: SignRequestsOption[]) =>
  values
    .map(
      (value) =>
        options.find((option) => option.value === value)?.label ?? value,
    )
    .join(", ");

/** The filters a user can add, in the order the bar and the drawer list them. */
export type SignRequestsFilterKey =
  "status" | "date" | "signer" | "senders" | "templates" | "tags";

export const SIGN_REQUESTS_FILTER_CATEGORIES: {
  key: SignRequestsFilterKey;
  label: string;
}[] = [
  { key: "status", label: "Status" },
  { key: "date", label: "Date created" },
  { key: "signer", label: "Signer" },
  { key: "senders", label: "Sender" },
  { key: "templates", label: "Template" },
  { key: "tags", label: "Tags" },
];

/** How many values one filter holds — 0 means it is not applied. */
export const getFilterValueCount = (
  filters: SignRequestsFilters,
  key: SignRequestsFilterKey,
) => {
  switch (key) {
    case "date":
      return filters.quick || filters.from || filters.to ? 1 : 0;
    case "signer":
      return filters.signer.trim() ? 1 : 0;
    default:
      return filters[key].length;
  }
};

/** The slice of the filters one category owns, reset to empty. */
export const getClearedFilter = (
  key: SignRequestsFilterKey,
): Partial<SignRequestsFilters> => {
  switch (key) {
    case "date":
      return { quick: null, from: "", to: "" };
    case "signer":
      return { signer: "" };
    default:
      return { [key]: [] } as Partial<SignRequestsFilters>;
  }
};

/** The word between a pill's label and its value: "is", "is any of", … */
export const getFilterOperator = (
  filters: SignRequestsFilters,
  key: SignRequestsFilterKey,
) => {
  if (key === "date") return filters.quick ? "in" : "between";
  if (key === "signer") return "contains";
  return filters[key].length > 1 ? "is any of" : "is";
};

/** "Oct 5 – Oct 12", "From Oct 5", or the quick range's own name. */
export const getDateFilterLabel = (filters: SignRequestsFilters) => {
  if (filters.quick) {
    return (
      SIGN_REQUESTS_QUICK_RANGES.find((range) => range.key === filters.quick)
        ?.label ?? ""
    );
  }
  if (filters.from && filters.to) {
    return `${formatShort(filters.from)} – ${formatShort(filters.to)}`;
  }
  if (filters.from) return `From ${formatShort(filters.from)}`;
  return filters.to ? `Until ${formatShort(filters.to)}` : "";
};

/** The dates a quick range covers today — the aside on its row. */
export const getQuickRangeHint = (quick: SignRequestsQuickRange) =>
  `${formatShort(getQuickRangeStart(quick))} – ${formatShort(getToday())}`;

/** One chip per applied filter group, in the drawer's own section order. */
export const getFilterChips = (
  filters: SignRequestsFilters,
  lookups: SignRequestsFilterLookups,
): SignRequestsFilterChip[] => {
  const chips: SignRequestsFilterChip[] = [];

  if (filters.status.length) {
    chips.push({
      key: "status",
      label: "Status",
      value: labelsFor(filters.status, SIGN_REQUESTS_STATUS_OPTIONS),
      cleared: { status: [] },
    });
  }
  if (filters.quick) {
    chips.push({
      key: "created",
      label: "Created",
      value:
        SIGN_REQUESTS_QUICK_RANGES.find((range) => range.key === filters.quick)
          ?.label ?? "",
      cleared: { quick: null, from: "", to: "" },
    });
  } else if (filters.from || filters.to) {
    chips.push({
      key: "created",
      label: "Created",
      value:
        filters.from && filters.to
          ? `${formatShort(filters.from)} – ${formatShort(filters.to)}`
          : filters.from
            ? `From ${formatShort(filters.from)}`
            : `Until ${formatShort(filters.to)}`,
      cleared: { from: "", to: "" },
    });
  }
  if (filters.signer.trim()) {
    chips.push({
      key: "signer",
      label: "Signer",
      value: filters.signer.trim(),
      cleared: { signer: "" },
    });
  }
  if (filters.senders.length) {
    chips.push({
      key: "senders",
      label: filters.senders.length > 1 ? "Senders" : "Sender",
      value: labelsFor(filters.senders, lookups.senders),
      cleared: { senders: [] },
    });
  }
  if (filters.templates.length) {
    chips.push({
      key: "templates",
      label: filters.templates.length > 1 ? "Templates" : "Template",
      value: labelsFor(filters.templates, lookups.templates),
      cleared: { templates: [] },
    });
  }
  if (filters.tags.length) {
    chips.push({
      key: "tags",
      label: filters.tags.length > 1 ? "Tags" : "Tag",
      value: labelsFor(
        filters.tags,
        lookups.tags.map((tag) => ({ value: tag.id, label: tag.name })),
      ),
      cleared: { tags: [] },
    });
  }
  return chips;
};

/** "2 filters · Newest first" — the line under a view's name. */
export const getViewSummary = (
  view: SignRequestsView,
  lookups: SignRequestsFilterLookups,
) => {
  const count = getFilterChips(view.filters, lookups).length;
  const filtersLabel = count
    ? `${count} ${count === 1 ? "filter" : "filters"}`
    : "No filters";
  return `${filtersLabel} · ${getSortLabel(view.sort)}`;
};

/** The screen's filters and sort order as the API body. Empty parts are omitted. */
export const toFilterBody = (
  filters: SignRequestsFilters,
  sort: SignRequestsSortKey,
): SignRequestFilterBody => {
  const from = filters.quick ? getQuickRangeStart(filters.quick) : filters.from;
  const to = filters.quick ? getToday() : filters.to;
  const signer = filters.signer.trim();

  return {
    ...(filters.status.length ? { statuses: filters.status } : {}),
    ...(from ? { dateFrom: from } : {}),
    ...(to ? { dateTo: to } : {}),
    ...(signer ? { signer } : {}),
    ...(filters.senders.length ? { senders: filters.senders } : {}),
    ...(filters.templates.length ? { templateIds: filters.templates } : {}),
    ...(filters.tags.length ? { tagIds: filters.tags } : {}),
    sort: sort.toUpperCase(),
  };
};

const isIsoDate = (value?: string | null): value is string =>
  !!value && moment(value, DATE_FORMAT, true).isValid();

/** An API filter body — a saved view's, or the last-used one — as the screen's model. */
export const fromFilterBody = (
  body?: SignRequestFilterBody | null,
): { filters: SignRequestsFilters; sort: SignRequestsSortKey } => ({
  filters: {
    status: (body?.statuses ?? []).filter((status) =>
      SIGN_REQUESTS_STATUS_OPTIONS.some((option) => option.value === status),
    ),
    quick: null,
    from: isIsoDate(body?.dateFrom) ? body!.dateFrom! : "",
    to: isIsoDate(body?.dateTo) ? body!.dateTo! : "",
    signer: body?.signer ?? "",
    senders: body?.senders ?? [],
    templates: body?.templateIds ?? [],
    tags: body?.tagIds ?? [],
  },
  sort: toSortKey(body?.sort),
});

export const viewFromApi = (view: {
  id: string;
  name: string;
  filters?: SignRequestFilterBody | null;
}): SignRequestsView => ({
  id: view.id,
  name: view.name,
  ...fromFilterBody(view.filters),
});

const splitParam = (value: string | null) =>
  (value ?? "")
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);

/**
 * Filters carried on the URL, which is how a filtered list is handed to
 * someone else:
 * `?status=sent,in_progress&from=…&to=…&signer=…&sender=…&template=…&tag=…&sort=…&view=…`.
 * `null` when the URL carries no filter at all.
 */
export const readSharedFilters = (
  search: string,
): {
  filters: SignRequestsFilters;
  sort: SignRequestsSortKey;
  viewId: string | null;
} | null => {
  const params = new URLSearchParams(search);
  const { filters } = fromFilterBody({
    statuses: splitParam(params.get("status")).map((status) =>
      status.toUpperCase(),
    ),
    dateFrom: params.get("from") ?? undefined,
    dateTo: params.get("to") ?? undefined,
    signer: params.get("signer") ?? undefined,
    senders: splitParam(params.get("sender")),
    templateIds: splitParam(params.get("template")),
    tagIds: splitParam(params.get("tag")),
  });
  const hasSort = params.has("sort");
  if (!hasSort && !hasAppliedFilters(filters)) return null;

  return {
    filters,
    sort: toSortKey(params.get("sort")),
    viewId: params.get("view"),
  };
};

export const hasAppliedFilters = (filters: SignRequestsFilters) =>
  Boolean(
    filters.status.length ||
    filters.quick ||
    filters.from ||
    filters.to ||
    filters.signer.trim() ||
    filters.senders.length ||
    filters.templates.length ||
    filters.tags.length,
  );

/** Maps a stored `#RRGGBB` onto its swatch; anything off-preset reads as the default. */
const toTagColor = (hex?: string | null): SignRequestsTagColor =>
  SIGN_REQUESTS_TAG_COLOR_KEYS.find(
    (key) =>
      signRequestsTagColors[key].hex.toLowerCase() ===
      (hex ?? "").toLowerCase(),
  ) ?? DEFAULT_TAG_COLOR;

export const toTagHex = (color: SignRequestsTagColor) =>
  signRequestsTagColors[color].hex;

export const tagFromApi = (tag: {
  id: string;
  name: string;
  color?: string | null;
  usageCount?: number | null;
  canManage?: boolean | null;
}): SignRequestTag => ({
  id: tag.id,
  name: tag.name,
  color: toTagColor(tag.color),
  ...(typeof tag.usageCount === "number" ? { usageCount: tag.usageCount } : {}),
  ...(typeof tag.canManage === "boolean" ? { canManage: tag.canManage } : {}),
});

/** A row of the filtered list: a request plus the three fields that list adds. */
type FilteredSignRequest = SignRequest & {
  uiStatus?: string | null;
  expiresOn?: string | null;
  tags?: Parameters<typeof tagFromApi>[0][] | null;
};

/** The request's `UiSignStatus`, when the row came from the filtered list. */
export const getRequestUiStatus = (request: SignRequest) =>
  (request as FilteredSignRequest).uiStatus ?? null;

/** Tags on a list row. Only the filtered list sends them. */
export const getRequestTags = (request: SignRequest): SignRequestTag[] =>
  ((request as FilteredSignRequest).tags ?? []).map(tagFromApi);

/** The row with its tag set swapped for the one the API just returned. */
export const withRequestTags = <T extends SignRequest>(
  request: T,
  tags: unknown,
): T => ({ ...request, tags }) as T;

export const isTagLimitReached = (tagIds: string[]) =>
  tagIds.length >= tagTokens.limit;

const parseCreatedOn = (raw: unknown) => {
  if (!raw) return null;
  const strict = moment(raw as string, CREATED_ON_FORMAT, true);
  if (strict.isValid()) return strict;
  const loose = moment(raw as string);
  return loose.isValid() ? loose : null;
};

/** Raw statuses the folder list uses for a request nobody is waiting on. */
const CLOSED_RAW_STATUSES = [
  "Draft",
  "Signed",
  "Completed",
  "Declined",
  "Rejected",
  "Expired",
  "Voided",
];

/**
 * The Expires cell: the date a request lapses and, for an open one inside the
 * warning window, how soon.
 *
 * The filtered list sends `expiresOn`. The folder list does not, so there the
 * date is derived the way the backend derives it — created date plus the
 * request's `expirationDays`.
 */
export const getRequestExpiry = (
  request: SignRequest,
): { label: string; soon: string } => {
  const { expiresOn } = request as FilteredSignRequest;
  const uiStatus = getRequestUiStatus(request);

  let expires = expiresOn ? moment(expiresOn, DATE_FORMAT) : null;
  if (!expires && !uiStatus && request.expirationDays > 0) {
    expires =
      parseCreatedOn(request.createdOn)
        ?.clone()
        .add(request.expirationDays, "days") ?? null;
  }
  if (!expires || !expires.isValid()) {
    return { label: table.emptyValue, soon: "" };
  }

  const isOpen = uiStatus
    ? OPEN_UI_STATUSES.includes(uiStatus)
    : !CLOSED_RAW_STATUSES.includes(request.status);
  const daysLeft = expires
    .clone()
    .startOf("day")
    .diff(moment().startOf("day"), "days");
  const isSoon = isOpen && daysLeft >= 0 && daysLeft <= table.soonWindowDays;

  return {
    label: expires.format(DISPLAY_DATE_FORMAT),
    soon: !isSoon
      ? ""
      : daysLeft === 0
        ? "Today"
        : daysLeft === 1
          ? "in 1 day"
          : `in ${daysLeft} days`,
  };
};
